import {
    Component,
    OnInit,
    OnDestroy,
    Input,
    Output,
    EventEmitter
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    FormControl,
    Validators
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import * as moment from 'moment';

import { BookingService } from '@app/_services/booking.service';
import { Product } from '@app/_interfaces/product';
import { takeUntil } from 'rxjs/operators';
import { CalendarDay, CalendarMonth } from '@app/_interfaces/calendar';
import { CompaniesService } from '@app/_services';
import { QuotationRequestService } from '@app/_services/quotation-request.service';
import { AuthenticationService } from '@app/_services';
import { Router } from '@angular/router';
import { InputNumberNewComponent } from '@app/shared/components/input-number-new/input-number-new.component';
import { CartService } from '@services/cart.service'; // Add cart service
import { FormDataHelper } from '@app/_helpers'; // Add form data helper
import { CurrencyConvertPipe } from '@app/shared/pipes';
import _ from 'lodash';

// interface CalendarDay {
//     date: string;
//     day: number;
//     isCurrentMonth: boolean;
//     isToday: boolean;
//     isAvailable: boolean;
//     isSelected: boolean;
//     isUnavailable: boolean;
// }

// interface CalendarMonth {
//     year: number;
//     month: number;
//     monthName: string;
//     days: CalendarDay[];
// }

// Interface for API response structure
interface BookingApiResponse {
    code: number;
    msg?: string;
    availableDates?: string[];
    unavailableDates?: Array<
        | {
              date: string;
              reason?: string;
              remainingQuantity?: number;
          }
        | string
    >;
    booking?: any;
}

@Component({
    selector: 'app-period-booking',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        InputNumberNewComponent,
        CurrencyConvertPipe
    ],
    templateUrl: './period-booking.component.html',
    styleUrl: './period-booking.component.scss'
})
export class PeriodBookingComponent implements OnInit, OnDestroy {
    // === INPUT/OUTPUT PROPERTIES ===

    /** Product being booked - contains all product and booking configuration */
    @Input() product: Product;

    /** Emits when a booking is successfully created */
    @Output() bookingCreated = new EventEmitter<any>();

    /** Emits when an item is added to cart */
    @Output() addedToCart = new EventEmitter<any>();

    /** Emits when the total price changes (for parent component price display) */
    @Output() priceCalculated = new EventEmitter<number>();

    /** Emits when selected dates change (for parent component synchronization) */
    @Output() selectedDatesChanged = new EventEmitter<string[]>();

    /** Emits when a quotation request is successfully created */
    @Output() quotationRequested = new EventEmitter<any>();

    // === FORM AND VALIDATION ===

    /** Reactive form for booking data with validation */
    bookingForm: FormGroup;

    /** Reactive form for quotation requests */
    quotationForm: FormGroup;

    /** Whether to show the quotation form modal */
    showQuotationForm = false;

    /** Form control for quantity selection */
    quantityControl: FormControl;

    /** Current selected quantity */
    get selectedQuantity(): number {
        return this.quantityControl?.value || 1;
    }

    // === AVAILABILITY DATA ===

    /** Array of dates that are available for booking (YYYY-MM-DD format) */
    availableDates: string[] = [];

    /** Array of unavailable dates with reasons for unavailability */
    unavailableDates: any[] = [];

    /** The start and end date selected by the user */
    startDate: string = '';
    endDate: string = '';

    /** Whether API requests are in progress */
    loading = false;

    /** Calculated total price for all selected dates */
    totalPrice = 0;

    /** Number of days in the selected period */
    totalDays = 0;

    /**
     * Tracks available quantity for each date
     * Key: date string (YYYY-MM-DD)
     * Value: number of items available for booking on that date
     */
    availableQuantityByDate: { [date: string]: number } = {};

    // === CALENDAR DISPLAY ===

    /** Current month index being displayed (0-2 for 3-month view) */
    currentMonthIndex = 0;

    /** Array of calendar months with all day information */
    calendarMonths: CalendarMonth[] = [];

    /** Week day headers for calendar display */
    weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    /** Number of future months to display in the calendar */
    monthsToGenerate = 1;

    /** Subject for handling component destruction and unsubscribing */
    private destroy$ = new Subject<void>();

    /** Selection state for period booking */
    isSelectingPeriod = false;
    hoveredDate: string = '';

    // === COMPUTED PROPERTIES ===

    /**
     * Gets the product's available quantity/stock
     * Used for calculating per-date availability
     *
     * @returns Number of items available for this product
     */
    get productQuantity(): number {
        const quantity = (this.product as any)?.quantity?.amount || 0;
        return Math.max(0, quantity);
    }

    /**
     * Checks if the product has any available quantity
     * @returns true if product has quantity > 0
     */
    get hasProductQuantity(): boolean {
        return this.productQuantity > 0;
    }

    /**
     * Gets the minimum booking period in days
     */
    get minBookingDays(): number {
        return this.product?.booking?.minBookingDays || 1;
    }

    /**
     * Gets the maximum booking period in days
     */
    get maxBookingDays(): number {
        return this.product?.booking?.maxBookingDays || 0; // 0 = no limit
    }

    /**
     * Checks if the product is set up for quotation instead of direct booking
     */
    get isQuotable(): boolean {
        return this.product?.isOnlyQuotation || false;
    }

    /**
     * Constructor - Sets up dependencies and initializes the booking form
     */
    constructor(
        private fb: FormBuilder,
        private bookingService: BookingService,
        private toastr: ToastrService,
        private translate: TranslateService,
        private companiesService: CompaniesService,
        private quotationRequestService: QuotationRequestService,
        private authService: AuthenticationService,
        private router: Router,
        private cartService: CartService, // Add cart service
        private formDataHelper: FormDataHelper // Add form data helper
    ) {
        this.initializeForm();
    }

    /**
     * Initializes the reactive form with validation rules
     */
    private initializeForm() {
        // Initialize quantity control
        this.quantityControl = new FormControl(
            this.product?.quantity?.min || 1,
            [
                Validators.min(this.product?.quantity?.min || 1),
                Validators.max(this.product?.quantity?.max || 99),
                Validators.required
            ]
        );

        this.bookingForm = this.fb.group({
            startDate: [''],
            endDate: [''],
            totalDays: [0],
            quantity: this.quantityControl // Add quantity to the form
        });

        this.quotationForm = this.fb.group({
            description: ['']
        });

        // Subscribe to quantity changes
        this.quantityControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(quantity => {
                this.calculatePeriodAndPrice();
                this.generateCalendarMonths(); // Refresh calendar to show availability based on new quantity
            });
    }

    /**
     * Updates the quantity control when product changes
     */
    private updateQuantityControl() {
        if (this.quantityControl && this.product?.quantity) {
            const min = this.product.quantity.min || 1;
            const max = this.product.quantity.max || 99;
            const step = this.product.quantity.step || 1;

            // Update validators
            this.quantityControl.setValidators([
                Validators.min(min),
                Validators.max(max),
                Validators.required
            ]);

            // Set initial value if current value is out of bounds
            const currentValue = this.quantityControl.value;
            if (currentValue < min || currentValue > max) {
                this.quantityControl.setValue(min);
            }

            this.quantityControl.updateValueAndValidity();
        }
    }

    /**
     * Gets the available quantity for a specific date
     */
    getAvailableQuantityForDate(date: string): number {
        return this.availableQuantityByDate[date] || 0;
    }

    /**
     * Generates calendar months for the next 3 months
     */
    private generateCalendarMonths() {
        this.calendarMonths = [];
        const startMonth = moment();

        for (let i = 0; i < this.monthsToGenerate; i++) {
            const month = startMonth.clone().add(i, 'month');
            this.calendarMonths.push(this.createCalendarMonth(month));
        }
    }

    /**
     * Checks if a date is within the selected period
     */
    private isDateInSelectedPeriod(date: string): boolean {
        if (!this.startDate || !this.endDate) {
            return date === this.startDate;
        }

        const checkDate = moment(date);
        const start = moment(this.startDate);
        const end = moment(this.endDate);

        return checkDate.isSameOrAfter(start) && checkDate.isSameOrBefore(end);
    }

    /**
     * Checks if a date is within the hover range (for visual feedback)
     */
    isDateInHoverRange(date: string): boolean {
        if (!this.startDate || !this.hoveredDate || this.endDate) {
            return false;
        }

        const checkDate = moment(date);
        const start = moment(this.startDate);
        const hovered = moment(this.hoveredDate);

        const rangeStart = moment.min(start, hovered);
        const rangeEnd = moment.max(start, hovered);

        return (
            checkDate.isSameOrAfter(rangeStart) &&
            checkDate.isSameOrBefore(rangeEnd)
        );
    }

    /**
     * Updates calendar days after selection changes
     */
    private updateCalendarDays() {
        this.calendarMonths.forEach(month => {
            month.days.forEach(day => {
                day.isSelected = this.isDateInSelectedPeriod(day.date);
            });
        });
    }

    /**
     * Handles date selection for period booking
     */
    onDateSelected(day: CalendarDay) {
        if (!day.isCurrentMonth || day.isUnavailable) {
            return;
        }

        if (!day.isAvailable) {
            this.toastr.warning(
                this.translate.instant('unavailable_fully_booked')
            );
            return;
        }

        // Check if date can accommodate the selected quantity
        if (!this.canDateAccommodateQuantity(day.date)) {
            this.toastr.warning(
                this.translate.instant('insufficient_quantity_for_date', {
                    date: moment(day.date).format('MMM DD'),
                    available: this.availableQuantityByDate[day.date] || 0,
                    required: this.selectedQuantity
                })
            );
            return;
        }

        if (!this.startDate) {
            // First date selection - set as start date
            this.startDate = day.date;
            this.endDate = '';
            this.isSelectingPeriod = true;
        } else if (!this.endDate) {
            // Second date selection - set as end date
            const startMoment = moment(this.startDate);
            const endMoment = moment(day.date);

            if (endMoment.isBefore(startMoment)) {
                // If selected date is before start date, make it the new start date
                this.startDate = day.date;
                this.endDate = '';
            } else {
                // Set as end date
                this.endDate = day.date;
                this.isSelectingPeriod = false;

                // Validate the period with quantity constraints
                if (!this.validatePeriodWithQuantity()) {
                    this.clearSelection();
                    return;
                }
            }
        } else {
            // Start new selection
            this.startDate = day.date;
            this.endDate = '';
            this.isSelectingPeriod = true;
        }

        // Update form
        this.bookingForm.patchValue(
            {
                startDate: this.startDate,
                endDate: this.endDate
            },
            { emitEvent: false }
        );

        this.updateCalendarDays();
        this.calculatePeriodAndPrice();
    }

    /**
     * Handles mouse hover for visual feedback
     */
    onDateHovered(day: CalendarDay) {
        if (!day.isCurrentMonth || !this.startDate || this.endDate) {
            this.hoveredDate = '';
            return;
        }

        this.hoveredDate = day.date;
        this.updateCalendarDays();
    }

    /**
     * Validates the selected period against business rules including quantity
     */
    private validatePeriodWithQuantity(): boolean {
        if (!this.startDate || !this.endDate) {
            return false;
        }

        const start = moment(this.startDate);
        const end = moment(this.endDate);
        const days = end.diff(start, 'days') + 1;

        // Check minimum booking days
        if (days < this.minBookingDays) {
            this.toastr.error(
                this.translate.instant('minimum_period_required', {
                    min: this.minBookingDays
                })
            );
            return false;
        }

        // Check maximum booking days
        if (this.maxBookingDays > 0 && days > this.maxBookingDays) {
            this.toastr.error(
                this.translate.instant('maximum_period_exceeded', {
                    max: this.maxBookingDays
                })
            );
            return false;
        }

        // Check advance booking requirements
        const advanceBookingDays =
            this.product?.booking?.advanceBookingDays || 0;
        const today = moment().startOf('day');
        const minBookingDate = today
            .clone()
            .add(advanceBookingDays + 1, 'days');

        if (start.isBefore(minBookingDate)) {
            this.toastr.error(
                this.translate.instant('book_in_advance', {
                    days: advanceBookingDays + 1
                })
            );
            return false;
        }

        // Check for unavailable dates in the period with quantity constraints
        const unavailableDatesInPeriod = this.getUnavailableDatesInPeriodWithQuantity();
        if (unavailableDatesInPeriod.length > 0) {
            const formattedDates = unavailableDatesInPeriod
                .map(date => moment(date).format('MMM DD'))
                .join(', ');

            this.toastr.error(
                this.translate.instant('period_unavailable_quantities', {
                    dates: formattedDates,
                    quantity: this.selectedQuantity
                })
            );
            return false;
        }

        return true;
    }

    /**
     * Gets unavailable dates within the selected period considering quantity requirements
     */
    private getUnavailableDatesInPeriodWithQuantity(): string[] {
        if (!this.startDate || !this.endDate) {
            return [];
        }

        const unavailableDates: string[] = [];
        const start = moment(this.startDate);
        const end = moment(this.endDate);
        let current = start.clone();

        while (current.isSameOrBefore(end)) {
            const dateString = current.format('YYYY-MM-DD');

            // Check if date can accommodate the selected quantity
            if (!this.canDateAccommodateQuantity(dateString)) {
                unavailableDates.push(dateString);
            }

            current.add(1, 'day');
        }

        return unavailableDates;
    }

    /**
     * Calculates period duration and total price with quantity
     */
    private calculatePeriodAndPrice() {
        if (!this.startDate || !this.endDate) {
            this.totalDays = 0;
            this.totalPrice = 0;
            this.priceCalculated.emit(0);
            this.selectedDatesChanged.emit([]);
            return;
        }

        const start = moment(this.startDate);
        const end = moment(this.endDate);
        this.totalDays = end.diff(start, 'days') + 1;

        // Calculate price - (daily rate × number of days) × quantity
        const dailyRate = this.product?.taxedPrice || this.product?.price || 0;
        this.totalPrice = dailyRate * this.totalDays * this.selectedQuantity;

        this.bookingForm.patchValue(
            {
                totalDays: this.totalDays
            },
            { emitEvent: false }
        );

        // Emit events for parent component
        this.priceCalculated.emit(this.totalPrice);
        this.selectedDatesChanged.emit(this.getDateRangeArray());
    }

    /**
     * Returns an array of date strings between startDate and endDate (inclusive)
     */
    private getDateRangeArray(): string[] {
        if (!this.startDate || !this.endDate) {
            return [];
        }
        const dates: string[] = [];
        let current = moment(this.startDate);
        const end = moment(this.endDate);
        while (current.isSameOrBefore(end)) {
            dates.push(current.format('YYYY-MM-DD'));
            current.add(1, 'day');
        }
        return dates;
    }

    /**
     * Enhanced method to create calendar days with quantity-aware availability
     */
    private createCalendarMonth(month: moment.Moment): CalendarMonth {
        const targetMonth = month
            .clone()
            .locale('en')
            .startOf('month');

        const monthName = targetMonth.format('MMMM');
        const year = targetMonth.year();
        const monthIndex = targetMonth.month();
        const days: CalendarDay[] = [];

        // Get month information
        const firstDayOfMonth = targetMonth.clone().startOf('month');
        const daysInMonth = targetMonth.daysInMonth();
        const startDayOfWeek = firstDayOfMonth.day();

        // Add padding days from previous month
        if (startDayOfWeek > 0) {
            const prevMonth = targetMonth.clone().subtract(1, 'month');
            const daysInPrevMonth = prevMonth.daysInMonth();

            for (let i = startDayOfWeek - 1; i >= 0; i--) {
                const day = daysInPrevMonth - i;
                const date = prevMonth.clone().date(day);
                const dateStr = date.format('YYYY-MM-DD');

                days.push({
                    date: dateStr,
                    day: day,
                    isAvailable: false,
                    isSelected: false,
                    isUnavailable: true,
                    isToday: false,
                    isCurrentMonth: false
                });
            }
        }

        // Add current month days with quantity-aware availability
        for (let day = 1; day <= daysInMonth; day++) {
            const date = targetMonth.clone().date(day);
            const dateStr = date.format('YYYY-MM-DD');

            const today = moment()
                .locale('en')
                .startOf('day');
            const isToday = date.isSame(today, 'day');

            // Apply business rules for availability with quantity
            if (this.productQuantity <= 0) {
                days.push({
                    date: dateStr,
                    day: day,
                    isAvailable: false,
                    isSelected: false,
                    isUnavailable: true,
                    isToday: isToday,
                    isCurrentMonth: true
                });
                continue;
            }

            // Check advance booking requirements
            const advanceBookingDays =
                this.product?.booking?.advanceBookingDays || 0;
            const minBookingDate = today
                .clone()
                .add(advanceBookingDays + 1, 'days');
            const isBeforeMinimumDate = date.isBefore(minBookingDate);

            // Check quantity availability
            const canAccommodateQuantity = this.canDateAccommodateQuantity(
                dateStr
            );
            const isSelected = this.isDateInSelectedPeriod(dateStr);

            // Determine final availability status
            let isAvailable = false;
            let isUnavailable = false;

            if (isBeforeMinimumDate) {
                isUnavailable = true;
            } else if (canAccommodateQuantity) {
                isAvailable = true;
            } else {
                isUnavailable = true;
            }

            days.push({
                date: dateStr,
                day: day,
                isAvailable: isAvailable,
                isSelected: isSelected,
                isUnavailable: isUnavailable,
                isToday: isToday,
                isCurrentMonth: true
            });
        }

        // Add padding days from next month to complete the 6-week grid
        const totalDaysNeeded = 42;
        const remainingDays = totalDaysNeeded - days.length;

        if (remainingDays > 0) {
            const nextMonth = targetMonth.clone().add(1, 'month');

            for (let day = 1; day <= remainingDays; day++) {
                const date = nextMonth.clone().date(day);
                const dateStr = date.format('YYYY-MM-DD');

                days.push({
                    date: dateStr,
                    day: day,
                    isAvailable: false,
                    isSelected: false,
                    isUnavailable: true,
                    isToday: false,
                    isCurrentMonth: false
                });
            }
        }

        return {
            year: year,
            month: monthIndex,
            monthName: monthName,
            days: days
        };
    }

    /**
     * Checks if a date can accommodate the selected quantity
     */
    canDateAccommodateQuantity(date: string): boolean {
        const availableQuantity = this.getAvailableQuantityForDate(date);
        return availableQuantity >= this.selectedQuantity;
    }

    /**
     * Loads available dates from the API for the next 3 months
     */
    private loadAvailableDates() {
        const productSlug =
            (this.product as any)?.seo?.slug ||
            (this.product as any)?.slug ||
            this.product?.sku ||
            String(this.product?.id || this.product?._id);

        if (!productSlug) {
            this.toastr.error(
                this.translate.instant('Product identifier not found')
            );
            return;
        }

        this.loading = true;

        const startDate = moment().format('YYYY-MM-DD');
        const endDate = moment()
            .add(this.monthsToGenerate, 'months')
            .format('YYYY-MM-DD');

        // Include quantity in the API request
        this.bookingService
            .getAvailableDates(
                productSlug,
                startDate,
                endDate,
                this.selectedQuantity
            ) // Add quantity parameter
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: BookingApiResponse) => {
                    if (response.code === 200) {
                        this.availableDates = response.availableDates || [];
                        this.unavailableDates = response.unavailableDates || [];
                        this.processAvailabilityByDate(response);
                    } else {
                        this.toastr.error(this.translate.instant(response.msg));
                        this.resetAvailabilityData();
                    }
                    this.generateCalendarMonths();
                    this.loading = false;
                },
                error: error => {
                    this.toastr.error(
                        this.translate.instant('Failed to load available dates')
                    );
                    this.resetAvailabilityData();
                    this.generateCalendarMonths();
                    this.loading = false;
                }
            });
    }

    /**
     * Processes the API response to build detailed availability information
     */
    private processAvailabilityByDate(response: BookingApiResponse) {
        this.availableQuantityByDate = {};

        if (this.productQuantity <= 0) {
            this.availableDates = [];
            this.unavailableDates = response.unavailableDates || [];

            return;
        }

        // Process available dates
        if (response.availableDates && Array.isArray(response.availableDates)) {
            response.availableDates.forEach(date => {
                this.availableQuantityByDate[date] = this.productQuantity;
            });
        }

        // Process unavailable dates (enhanced to handle backend response format)
        if (
            response.unavailableDates &&
            Array.isArray(response.unavailableDates)
        ) {
            response.unavailableDates.forEach((unavailableDate: any) => {
                const date = unavailableDate.date || unavailableDate;

                if (
                    typeof unavailableDate === 'object' &&
                    unavailableDate.remainingQuantity !== undefined
                ) {
                    // Handle partial availability
                    const remaining = Math.max(
                        0,
                        unavailableDate.remainingQuantity
                    );
                    this.availableQuantityByDate[date] = remaining;

                    // Add to available dates if has remaining quantity
                    if (remaining > 0 && !this.availableDates.includes(date)) {
                        this.availableDates.push(date);
                    }
                } else {
                    // Fully unavailable
                    this.availableQuantityByDate[date] = 0;
                }
            });
        }
    }

    /**
     * Helper method to reset availability data structures
     */
    private resetAvailabilityData() {
        this.availableDates = [];
        this.unavailableDates = [];
        this.availableQuantityByDate = {};
    }

    /**
     * Validates the selected period against business rules
     */
    private validatePeriod(): boolean {
        if (!this.startDate || !this.endDate) {
            return false;
        }

        const start = moment(this.startDate);
        const end = moment(this.endDate);
        const days = end.diff(start, 'days') + 1;

        // Check minimum booking days
        if (days < this.minBookingDays) {
            this.toastr.error(
                this.translate.instant('minimum_period_required', {
                    min: this.minBookingDays
                })
            );
            return false;
        }

        // Check maximum booking days
        if (this.maxBookingDays > 0 && days > this.maxBookingDays) {
            this.toastr.error(
                this.translate.instant('maximum_period_exceeded', {
                    max: this.maxBookingDays
                })
            );
            return false;
        }

        // Check advance booking requirements (match backend logic)
        const advanceBookingDays =
            this.product?.booking?.advanceBookingDays || 0;
        const today = moment().startOf('day');
        const minBookingDate = today
            .clone()
            .add(advanceBookingDays + 1, 'days');

        if (start.isBefore(minBookingDate)) {
            this.toastr.error(
                this.translate.instant('book_in_advance', {
                    days: advanceBookingDays + 1
                })
            );
            return false;
        }

        // Check for unavailable dates in the period (detailed check)
        const unavailableDatesInPeriod = this.getUnavailableDatesInPeriod();
        if (unavailableDatesInPeriod.length > 0) {
            this.toastr.error(
                this.translate.instant('selected_period_unavailable')
            );

            return false;
        }

        return true;
    }

    /**
     * Gets unavailable dates within the selected period (enhanced to match backend)
     */
    private getUnavailableDatesInPeriod(): string[] {
        if (!this.startDate || !this.endDate) {
            return [];
        }

        const unavailableDates: string[] = [];
        const start = moment(this.startDate);
        const end = moment(this.endDate);
        let current = start.clone();

        while (current.isSameOrBefore(end)) {
            const dateString = current.format('YYYY-MM-DD');

            // Check if date is in available dates list
            const isAvailable = this.availableDates.includes(dateString);
            const hasQuantity =
                this.getAvailableQuantityForDate(dateString) > 0;

            // Date is unavailable if it's not in available list OR has no quantity
            if (!isAvailable && !hasQuantity) {
                unavailableDates.push(dateString);
            }

            current.add(1, 'day');
        }

        return unavailableDates;
    }

    /**
     * Clears all selected dates
     */
    clearSelection() {
        this.startDate = '';
        this.endDate = '';
        this.totalDays = 0;
        this.totalPrice = 0;
        this.isSelectingPeriod = false;
        this.hoveredDate = '';

        this.bookingForm.patchValue(
            {
                startDate: '',
                endDate: '',
                totalDays: 0
            },
            { emitEvent: false }
        );

        this.updateCalendarDays();
        this.priceCalculated.emit(0);
        this.selectedDatesChanged.emit([]);
    }

    /**
     * Creates a new period booking or quotation request or adds to cart
     */
    createBooking() {
        if (!this.startDate || !this.endDate) {
            this.toastr.error(
                this.translate.instant('select_valid_booking_period')
            );
            return;
        }

        if (!this.validatePeriod()) {
            return;
        }

        // Check if user is authenticated for quotation requests
        if (this.isQuotable && !this.authService.currentUserValue) {
            this.router.navigate(['/login'], {
                queryParams: { returnUrl: this.router.url }
            });
            return;
        }

        if (this.isQuotable) {
            this.showQuotationForm = true;
        } else {
            // For non-quotation products, add to cart instead of direct booking
            this.loading = true;
            this.addBookingToCart();
        }
    }

    /**
     * Adds the booking item to cart instead of creating a direct booking
     */
    private addBookingToCart() {
        try {
            // Manual FormData creation to ensure proper structure
            const formData = new FormData();

            // Add base properties
            formData.append('productId', this.product._id);
            formData.append('amount', this.selectedQuantity.toString());

            // Add booking properties directly as individual fields
            formData.append('bookingType', 'period');
            formData.append('startDate', this.startDate);
            formData.append('endDate', this.endDate);
            formData.append('totalDays', this.totalDays.toString());
            formData.append(
                'bookingQuantity',
                this.selectedQuantity.toString()
            );
            formData.append('totalPrice', this.totalPrice.toString());

            // Add booking dates as individual entries
            const dates = this.getDateRangeArray();
            dates.forEach((date, index) => {
                formData.append(`bookingDates[${index}]`, date);
            });

            // Also add as JSON string for backward compatibility
            const bookingData = {
                bookingType: 'period',
                startDate: this.startDate,
                endDate: this.endDate,
                totalDays: this.totalDays,
                quantity: this.selectedQuantity,
                totalPrice: this.totalPrice,
                dates: dates
            };
            formData.append('bookingData', JSON.stringify(bookingData));

            this.cartService.addItem(formData, this.product).subscribe({
                next: (response: any) => {
                    if (response.cart) {
                        // Log the specific item that was added
                        if (response.cart.items) {
                            const addedItem =
                                response.cart.items[
                                    response.cart.items.length - 1
                                ];
                        }
                    }

                    this.loading = false;

                    if (response.msg && response.code === 400) {
                        this.toastr.error(response.msg);
                        return;
                    }

                    // Handle cart warnings and errors
                    if (response.cart?.warnings?.length > 0) {
                        response.cart.warnings.forEach(warning => {
                            this.toastr.warning(
                                warning.description[this.translate.currentLang],
                                warning.title[this.translate.currentLang]
                            );
                        });
                    }

                    if (response.cart?.errors?.length > 0) {
                        response.cart.errors.forEach(error => {
                            this.toastr.error(
                                error.description[this.translate.currentLang],
                                error.title[this.translate.currentLang]
                            );
                        });
                        return;
                    }

                    // Success
                    this.toastr.success(
                        this.translate.instant(
                            'booking_added_to_cart_success',
                            {
                                productName:
                                    this.product.name[
                                        this.translate.currentLang
                                    ] || this.product.name,
                                startDate: moment(this.startDate).format(
                                    'MMM DD'
                                ),
                                endDate: moment(this.endDate).format(
                                    'MMM DD, YYYY'
                                )
                            }
                        )
                    );

                    this.addedToCart.emit({
                        cartItem: {
                            productId: this.product._id,
                            amount: this.selectedQuantity,
                            bookingType: 'period',
                            startDate: this.startDate,
                            endDate: this.endDate,
                            totalDays: this.totalDays,
                            bookingQuantity: this.selectedQuantity,
                            totalPrice: this.totalPrice,
                            bookingDates: dates,
                            bookingData: bookingData
                        },
                        response
                    });

                    this.resetForm();
                },
                error: (error: any) => {
                    this.loading = false;
                    this.toastr.error(
                        this.translate.instant('failed_to_add_booking_to_cart')
                    );
                }
            });
        } catch (error) {
            this.loading = false;
            this.toastr.error(
                this.translate.instant('failed_to_prepare_booking')
            );
        }
    }

    /**
     * Closes the quotation form modal
     */
    closeQuotationForm(): void {
        this.showQuotationForm = false;
        this.quotationForm.reset();
    }

    /**
     * Submits the quotation request with user inputs
     */
    submitQuotationRequest(): void {
        this.loading = true;
        this.createQuotationRequest();
    }

    public buyNow() {
        if (this.loading) return;
        if (!this.startDate || !this.endDate) {
            this.toastr.error(
                this.translate.instant('select_valid_booking_period')
            );
            return;
        }
        if (this.isQuotable) {
            this.toastr.error(this.translate.instant('cannot_buy_quotation'));
            return;
        }

        this.loading = true;

        try {
            const formData = new FormData();
            formData.append('productId', String(this.product._id));
            formData.append('amount', String(this.selectedQuantity));
            formData.append('bookingType', 'period');
            formData.append('startDate', String(this.startDate));
            formData.append('endDate', String(this.endDate));
            formData.append('totalDays', String(this.totalDays));
            formData.append('bookingQuantity', String(this.selectedQuantity));
            // ensure single scalar values
            formData.set('totalPrice', String(this.totalPrice));

            const dates = this.getDateRangeArray();
            dates.forEach((d, i) => formData.append(`bookingDates[${i}]`, d));

            const bookingData = {
                bookingType: 'period',
                startDate: this.startDate,
                endDate: this.endDate,
                totalDays: this.totalDays,
                quantity: this.selectedQuantity,
                totalPrice: this.totalPrice,
                dates
            };
            formData.set('bookingData', JSON.stringify(bookingData));

            // mark single-checkout so backend creates an isolated cart for immediate checkout
            formData.set('singleCheckout', 'true');

            this.cartService.addItem(formData, this.product).subscribe({
                next: (response: any) => {
                    this.loading = false;

                    if (response.msg && response.code === 400) {
                        this.toastr.error(response.msg);
                        return;
                    }

                    if (response.cart?.warnings?.length > 0) {
                        response.cart.warnings.forEach(w =>
                            this.toastr.warning(
                                w.description[this.translate.currentLang],
                                w.title[this.translate.currentLang]
                            )
                        );
                    }

                    if (response.cart?.errors?.length > 0) {
                        response.cart.errors.forEach(e =>
                            this.toastr.error(
                                e.description[this.translate.currentLang],
                                e.title[this.translate.currentLang]
                            )
                        );
                        return;
                    }

                    const returnedCart = response.cart;
                    if (!returnedCart || !returnedCart._id) {
                        this.toastr.error(
                            this.translate.instant('failed_to_add_to_cart')
                        );
                        return;
                    }

                    // Robust matching: exact match by productId + bookingType + start/end/dates + bookingQuantity + totalPrice
                    const normalizedDates = (dates || []).map(String);
                    const addedItem = _.find(
                        returnedCart.items || [],
                        (it: any) => {
                            try {
                                if (
                                    String(it.productId) !==
                                    String(this.product._id)
                                )
                                    return false;
                                if (it.bookingType !== 'period') return false;
                                if (
                                    (it.startDate && String(it.startDate)) !==
                                    String(this.startDate)
                                )
                                    return false;
                                if (
                                    (it.endDate && String(it.endDate)) !==
                                    String(this.endDate)
                                )
                                    return false;
                                const itemDates =
                                    (it.bookingDates &&
                                        it.bookingDates.map(String)) ||
                                    (it.bookingData &&
                                        it.bookingData.dates &&
                                        it.bookingData.dates.map(String)) ||
                                    [];
                                const datesEqual =
                                    _.isEqual(itemDates, normalizedDates) ||
                                    _.isEqual(
                                        _.sortBy(itemDates),
                                        _.sortBy(normalizedDates)
                                    );
                                if (!datesEqual) return false;
                                if (
                                    (it.bookingQuantity || it.amount || 0) !==
                                    (this.selectedQuantity || 0)
                                )
                                    return false;
                                const itemTotalPrice = Number(
                                    it.totalPrice ??
                                        it.total ??
                                        it.unitTotal ??
                                        0
                                );

                                if (
                                    Math.abs(
                                        itemTotalPrice -
                                            Number(this.totalPrice || 0)
                                    ) > 0.0001
                                )
                                    return false;
                                return true;
                            } catch (e) {
                                return false;
                            }
                        }
                    );

                    // fallback: last matching productId & period bookingType
                    const fallback = _.findLast(
                        returnedCart.items || [],
                        (it: any) =>
                            String(it.productId) === String(this.product._id) &&
                            it.bookingType === 'period'
                    );

                    const targetItem = addedItem || fallback;
                    if (!targetItem) {
                        this.toastr.success(
                            this.translate.instant('added_to_cart')
                        );
                        const params = {
                            cartId: returnedCart._id,
                            itemIds: []
                        };
                        this.cartService.userCart = {
                            cartId: returnedCart._id,
                            items: []
                        };
                        this.cartService.saveCartId(params).subscribe(() => {
                            this.router.navigate(['/checkout'], {
                                state: { data: params }
                            });
                        });
                        return;
                    }

                    // Checkout only the found item
                    this.checkoutSingleItem(
                        returnedCart._id,
                        targetItem.itemId
                    );
                },
                error: (err: any) => {
                    this.loading = false;
                    console.error('BuyNow addBookingToCart error', err);
                    this.toastr.error(
                        this.translate.instant('failed_to_add_booking_to_cart')
                    );
                }
            });
        } catch (err) {
            this.loading = false;
            console.error('BuyNow prepare error', err);
            this.toastr.error(
                this.translate.instant('failed_to_prepare_booking')
            );
        }
    }

    /**
     * Checkout a single item from a cart (sets userCart and navigates to checkout)
     */
    private checkoutSingleItem(cartId: string, itemId: string) {
        const items = [itemId];
        const params = { cartId, itemIds: items };
        this.cartService.userCart = { cartId, items };
        this.cartService.saveCartId(params).subscribe({
            next: () => {
                this.router.navigate(['/checkout'], {
                    state: { data: params }
                });
            },
            error: () => {
                this.router.navigate(['/checkout'], {
                    state: { data: params }
                });
            }
        });
    }

    /**
     * Creates an actual booking (non-quotation)
     */
    private createActualBooking() {
        // Get company name first, then create booking
        this.companiesService.getCompanyName(this.product.companyId).subscribe({
            next: companyName => {
                console.log('Company Name:', companyName);

                // Now create booking with the resolved company name
                const bookingData = {
                    productId: String(
                        (this.product as any)._id || this.product?.id
                    ),
                    companyId:
                        (this.product as any)?.companyId ||
                        (this.product as any)?.companyData?._id,
                    bookingType: 'period' as const,
                    startDate: this.startDate,
                    endDate: this.endDate,
                    quantity: this.selectedQuantity, // Include quantity
                    totalPrice: this.totalPrice,
                    specialRequests: '',
                    customerName: '',
                    customerEmail: '',
                    productName:
                        typeof this.product.name === 'object' &&
                        this.product.name !== null
                            ? {
                                  en:
                                      (this.product.name as {
                                          en?: string;
                                          ar?: string;
                                      }).en || '',
                                  ar:
                                      (this.product.name as {
                                          en?: string;
                                          ar?: string;
                                      }).ar || ''
                              }
                            : {
                                  en: String(this.product.name || ''),
                                  ar: String(this.product.name || '')
                              },
                    companyName: {
                        en: companyName.en || '',
                        ar: companyName.ar || ''
                    }
                };

                console.log('Period booking data with quantity:', bookingData);

                this.bookingService.createBooking(bookingData).subscribe({
                    next: (response: BookingApiResponse) => {
                        this.loading = false;
                        if (response && response.code === 200) {
                            this.toastr.success(
                                this.translate.instant(
                                    'booking_created_successfully'
                                )
                            );
                            this.bookingCreated.emit(response.booking);
                            this.resetForm();
                        } else {
                            this.toastr.error(
                                response?.msg ||
                                    this.translate.instant(
                                        'failed_to_create_booking'
                                    )
                            );
                        }
                    },
                    error: (error: any) => {
                        this.loading = false;
                        this.toastr.error(
                            this.translate.instant(
                                'booking_network_error_period'
                            )
                        );
                    }
                });
            },
            error: error => {
                console.error('Failed to get company name:', error);
                this.loading = false;
                this.toastr.error(
                    this.translate.instant('failed_to_get_company_info')
                );
            }
        });
    }

    /**
     * Creates a quotation request with quantity information
     */
    private createQuotationRequest() {
        const description = this.quotationForm.get('description')?.value || '';

        const quotationData = {
            product: {
                _id: (this.product as any)._id || this.product?.id,
                name:
                    typeof this.product.name === 'object' &&
                    this.product.name !== null
                        ? {
                              en:
                                  (this.product.name as {
                                      en?: string;
                                      ar?: string;
                                  }).en || '',
                              ar:
                                  (this.product.name as {
                                      en?: string;
                                      ar?: string;
                                  }).ar || ''
                          }
                        : {
                              en: String(this.product.name || ''),
                              ar: String(this.product.name || '')
                          },
                qty: this.selectedQuantity, // Include quantity in quotation
                bookingDetails: {
                    type: 'period',
                    startDate: this.startDate,
                    endDate: this.endDate,
                    totalDays: this.totalDays,
                    quantity: this.selectedQuantity // Include quantity in booking details
                }
            },
            companyId:
                this.product?.companyId ||
                (this.product as any)?.companyData?._id,
            description: description
        };

        this.quotationRequestService.singleRequest(quotationData).subscribe({
            next: (response: any) => {
                this.loading = false;
                this.showQuotationForm = false;
                if (response && response.code !== 400) {
                    if (response.addedNotification) {
                        this.authService.updateNotifications({
                            added: response.addedNotification
                        });
                    }
                    this.toastr.success(
                        this.translate.instant(
                            'quotation_request_sent_successfully'
                        )
                    );
                    this.quotationRequested.emit(response);
                    this.resetForm();
                } else {
                    this.toastr.error(
                        response?.msg ||
                            this.translate.instant(
                                'failed_to_send_quotation_request'
                            )
                    );
                }
            },
            error: (error: any) => {
                this.loading = false;
                this.showQuotationForm = false;
                this.toastr.error(
                    this.translate.instant('quotation_request_network_error')
                );
            }
        });
    }

    /**
     * Resets the form to its initial state
     */
    private resetForm() {
        this.clearSelection();
        this.bookingForm.reset();
        this.loadAvailableDates();
    }

    // === CALENDAR NAVIGATION METHODS ===

    goToPreviousMonth() {
        if (this.currentMonthIndex > 0) {
            this.currentMonthIndex--;
        }
    }

    goToNextMonth() {
        if (this.currentMonthIndex < 2) {
            this.currentMonthIndex++;
        }
    }

    canGoPrevious(): boolean {
        return this.currentMonthIndex > 0;
    }

    canGoNext(): boolean {
        return this.currentMonthIndex < 2;
    }

    getCurrentMonth(): CalendarMonth {
        return (
            this.calendarMonths[this.currentMonthIndex] || {
                year: 0,
                month: 0,
                monthName: '',
                days: []
            }
        );
    }

    // === UI HELPER METHODS ===

    getTranslatedMonthYear(): string {
        const month = this.getCurrentMonth();
        if (!month.monthName) return '';

        // Use the monthName directly since it's already a string
        return `${month.monthName} ${month.year}`;
    }

    getTranslatedWeekDays(): string[] {
        return ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map(day =>
            this.translate.instant(day)
        );
    }

    getFormattedDateRange(): string {
        if (!this.startDate || !this.endDate) {
            return '';
        }

        const start = moment(this.startDate).format('MMM DD');
        const end = moment(this.endDate).format('MMM DD, YYYY');

        return `${start} - ${end}`;
    }

    getConstraintsText(): string {
        const constraints: string[] = [];

        if (this.minBookingDays > 1) {
            constraints.push(
                this.translate.instant('minimum_booking_period') +
                    ` ${this.minBookingDays} ` +
                    this.translate.instant(
                        this.minBookingDays === 1 ? 'day' : 'days'
                    )
            );
        }

        if (this.maxBookingDays > 0) {
            constraints.push(
                this.translate.instant('maximum_booking_period') +
                    ` ${this.maxBookingDays} ` +
                    this.translate.instant('days')
            );
        }

        const advanceBookingDays =
            this.product?.booking?.advanceBookingDays || 0;
        if (advanceBookingDays > 0) {
            constraints.push(
                this.translate.instant('book_in_advance', {
                    days: advanceBookingDays
                })
            );
        }

        return constraints.join(' • ');
    }

    /**
     * Enhanced tooltip text that includes quantity information
     */
    getDateTitle(day: CalendarDay): string {
        if (!day.isCurrentMonth) {
            return '';
        }

        if (day.isUnavailable) {
            const unavailableItem = this.unavailableDates.find(
                item => (item.date || item) === day.date
            );

            // Check if it's unavailable due to insufficient quantity
            const availableQuantity = this.getAvailableQuantityForDate(
                day.date
            );
            const selectedQty = this.selectedQuantity;

            if (availableQuantity > 0 && availableQuantity < selectedQty) {
                return this.translate.instant('insufficient_quantity', {
                    available: availableQuantity,
                    required: selectedQty
                });
            }

            return (
                unavailableItem?.reason || this.translate.instant('unavailable')
            );
        }

        if (day.isAvailable) {
            const quantity = this.getAvailableQuantityForDate(day.date);
            const selectedQty = this.selectedQuantity;

            if (quantity >= selectedQty) {
                if (selectedQty === 1) {
                    return this.translate.instant('available_items_click', {
                        quantity: quantity
                    });
                } else {
                    return this.translate.instant('available_for_quantity', {
                        available: quantity,
                        required: selectedQty
                    });
                }
            }
        }

        return '';
    }

    ngOnInit() {
        this.monthsToGenerate = this.product?.booking?.monthsToGenerate || 3;
        // Update quantity control when product changes
        this.updateQuantityControl();

        if (
            this.product?.booking?.forBooking &&
            this.product?.booking?.bookingType === 'period' &&
            this.hasProductQuantity
        ) {
            this.loadAvailableDates();
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
